import React, { Component, useState, useEffect, useCallback, useMemo } from 'react';
import Session from '../auth/Session';
import utilities from '../util/utilities';
import { FaSort } from 'react-icons/fa'
import '../layout/site.css';
import AuthFunctions from '../services/AuthService';
import {
    Container, Card, CardBody, CardTitle, Button,
    Table, Modal, ModalHeader, ModalBody, Spinner, Row, Col, Input, FormGroup, Label
} from "reactstrap";
import TransactionTypeForm from "./TransactionTypeForm";
import useSortableData from "./useSortableData";
import SortableHeader from "./SortableHeader";

const TransactionTypeGrid = () => {
    const apiBaseUrl = '';
    const [transactionTypes, setTransactionTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [includeInactive, setIncludeInactive] = useState(false);

    const [editing, setEditing] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingTransType, setEditingTransType] = useState(null);

    const sortGetters = useMemo(() => ({
        adjustmentDirection: (tt) => (tt.isPositiveAdjustment === false ? "Negative" : "Positive"),
        checkingLinked: (tt) => (tt.qbBillItemId ? "Linked" : "Not linked"),
        status: (tt) => (tt.isActive === false ? "Inactive" : "Active"),
    }), []);

    const { items: sortedTransactionTypes, sortConfig, requestSort } = useSortableData(
        transactionTypes,
        { key: "transactionName", direction: "asc", getters: sortGetters }
    );

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const token = await Session.getToken();

            const response = await fetch(`/api/TransactionType/GetTransactionTypesAsync?includeInactive=${includeInactive}`, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setTransactionTypes(data)
            }
        } catch (e) {
            console.error("Failed to load transaction types", e);
        } finally {
            setLoading(false);
        }
    }, [includeInactive]);

    useEffect(() => {
        loadData();
    }, [loadData]);


    const handleEdit = (transactionType) => {
        setEditingTransType(transactionType);
        setModalOpen(true);
    };

    const handleNew = () => {
        setEditingTransType(null);
        setModalOpen(true);
    };

    const handleSaved = () => {
        setModalOpen(false);
        loadData();
    };

    return (
        <Container className="mt-5">
            <Card>
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle tag="h5">Transaction Type Management</CardTitle>
                        <Button color="primary" onClick={handleNew}>+ New Transaction Type</Button>
                    </div>
                    <div className="mb-3">
                        <FormGroup check className="mb-0">
                            <Label check>
                                <Input
                                    id="includeInactiveTransactionTypes"
                                    type="checkbox"
                                    checked={includeInactive}
                                    onChange={(e) => setIncludeInactive(e.target.checked)}
                                />{" "}
                                Show inactive transaction types
                            </Label>
                        </FormGroup>
                    </div>

                    <Table bordered striped>
                        <thead>
                            <tr>
                                <SortableHeader label="Transaction Type" sortKey="transactionName" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableHeader label="QuickBooks Type" sortKey="quickBooksType" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Checking Category" sortKey="checkingLinked" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Adjustment Direction" sortKey="adjustmentDirection" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedTransactionTypes.map(tt => (
                                <tr key={tt.id} className={tt.isActive === false ? 'text-muted fst-italic' : ''}>
                                    <td>{tt.transactionName}</td>
                                    <td>{tt.quickBooksType}</td>
                                    <td>{tt.qbBillItemId ? <span className="text-success">Linked</span> : <span className="text-danger">Not linked</span>}</td>
                                    <td>{tt.isPositiveAdjustment === false ? "Negative" : "Positive"}</td>
                                    <td>{tt.isActive === false ? <span className="text-muted">Inactive</span> : "Active"}</td>
                                    <td>
                                        <Button size="sm" color="info" onClick={() => handleEdit(tt)}>Edit</Button>{" "}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
                        <ModalHeader toggle={() => setModalOpen(false)}>
                            {editingTransType ? "Edit Transaction Type" : "New Transaction Type"}
                        </ModalHeader>
                        <ModalBody>
                            <TransactionTypeForm
                                transactionTypeItem={editingTransType}
                                apiBaseUrl={apiBaseUrl}
                                onSuccess={handleSaved}
                                onCancel={() => setModalOpen(false)}
                            />
                        </ModalBody>
                    </Modal>
                </CardBody>
            </Card>
        </Container>
    )
}

export default TransactionTypeGrid;
